import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FormSelect,
  Icon,
  P,
  Stack,
  Tooltip,
} from '@fashionnovateam/nova-ui-toolkit';
import { ItemLeadTimeFormValues } from '../../types';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import CountryList from '../../../../Lib/Constants/countries';
import { FormSection } from '../components/FormSection';
import { useRequestedItemsContext } from '../../hooks/useRequestedItems';
import { DELIVERY_DATE_TOOLTIP } from '../../../../Lib/Constants/ui-text';
import { shipmentModeSelectOptions } from '../../constants';

type LogisticsSectionProps = {
  disabled?: boolean;
};

export function LogisticsSection({ disabled }: LogisticsSectionProps) {
  const { control } = useFormContext<ItemLeadTimeFormValues>();
  const { domesticEligible } = useRequestedItemsContext();

  const countrySelectItems = useMemo(
    () => [
      { value: '', label: 'Select..' },
      ...CountryList.map((country) => ({
        value: country.text,
        label: country.text,
        disabled: !domesticEligible && country.value === 'US',
      })),
    ],
    [domesticEligible],
  );

  return (
    <FormSection
      icon="truck/truck-default"
      title="Logistics"
      gradientFrom="from-[#EBEBEB]"
      gradientTo="to-[#F8F8F8]"
      bgColor="bg-neutral-100"
    >
      <Stack direction="row" gap="md">
        <div className="w-[50%]">
          <Controller
            control={control}
            name="country_of_origin"
            render={({ field, fieldState: { error } }) => (
              <FormSelect
                label="Country of Origin"
                inputId="country_of_origin"
                options={countrySelectItems}
                required
                value={field.value || ''}
                disabled={disabled}
                error={error?.message}
                onChangeValue={field.onChange}
                className="w-full"
              />
            )}
          />
        </div>
        <div className="w-[50%]">
          <Controller
            control={control}
            name="shipment_mode"
            render={({ field, fieldState: { error } }) => (
              <FormSelect
                label="Shipment Mode"
                inputId="shipment_mode"
                options={shipmentModeSelectOptions}
                required
                value={field.value || ''}
                disabled={disabled}
                error={error?.message}
                onChangeValue={field.onChange}
                className="w-full"
              />
            )}
          />
        </div>
      </Stack>

      <Stack direction="row" gap="md">
        <div className="w-[50%]">
          <ControlledFormInput
            control={control}
            name="custom_cut_lead_time_in_weeks"
            label="Lead Time"
            inputType="number"
            required
            disabled={disabled}
            inputProps={{
              className: 'w-full',
              min: '1',
              max: '50',
              endAdornment: (
                <P size="xs" color="disabled">
                  weeks
                </P>
              ),
            }}
          />
        </div>
        <div className="w-[50%]">
          <div className="flex items-center gap-1 mb-1">
            <P size="sm" weight="medium" className="text-base-primary">
              Delivery Date
            </P>
            <P size="sm" color="error">
              *
            </P>
            <Tooltip
              content={DELIVERY_DATE_TOOLTIP}
              position="top"
              arrow
              zIndex={60}
            >
              <Icon.InformationCircle className="text-neutral-400 cursor-help text-sm flex-shrink-0" />
            </Tooltip>
          </div>
          <ControlledFormInput
            control={control}
            name="earliest_custom_cut_delivery_date"
            label=""
            inputType="date"
            disabled={disabled}
            inputProps={{
              className: 'w-full',
              containerClassName: 'w-full',
            }}
          />
        </div>
      </Stack>

      <Stack direction="row" gap="md">
        <ControlledFormInput
          control={control}
          name="custom_MOQ_jr"
          label="JR MOQ"
          inputType="number"
          disabled={disabled}
          inputProps={{
            className: 'w-full',
            min: '0',
            endAdornment: (
              <P size="xs" color="disabled">
                units
              </P>
            ),
          }}
        />
        <ControlledFormInput
          control={control}
          name="custom_MOQ_plus"
          label="Plus MOQ"
          inputType="number"
          disabled={disabled}
          inputProps={{
            className: 'w-full',
            min: '0',
            endAdornment: (
              <P size="xs" color="disabled">
                units
              </P>
            ),
          }}
        />
      </Stack>
    </FormSection>
  );
}
